// RibForm — ребро жёсткости (rib) и перемычка (web).
//
// Обе операции работают с НЕЗАМКНУТОЙ линией эскиза: ребро рисуют одной
// чертой поперёк корпуса, а не обводят прямоугольником. Отличаются только
// направлением относительно плоскости эскиза — так же, как в Fusion:
//
//   ребро  — растёт ВНИЗ, к телу (черта по верхней грани корпуса);
//   перемычка — растёт ВВЕРХ от плоскости эскиза.
//
// Прямо нужны для печатных корпусов: без рёбер тонкая стенка ведёт.
import React from 'react';
import useCadStore from '../store/useCadStore';
import { getGeometryClient } from '../geometryClient';
import { findOpenChains } from '../sketch/sketchProfiles';
import { toast } from './Toasts';
import RU from '../i18n/ru';
import ExprInput from './ExprInput';
import cl from './Properties.module.css';

const FormRow = ({ label, children }) => (
    <div className={cl.formRow}>
        <div className={cl.formLabel}>{label}</div>
        <div className={cl.formField}>{children}</div>
    </div>
);

export default function RibForm() {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const sketches = useCadStore((s) => s.doc.sketches);
    const selection = useCadStore((s) => s.selection);
    const addFeature = useCadStore((s) => s.addFeature);

    const ribKind = pendingFeature?.ribKind || 'rib';
    const thickness = pendingFeature?.thickness ?? 2;
    const depth = pendingFeature?.depth ?? 10;
    const targetBodyId = pendingFeature?.targetBodyId || null;

    // Тело для приварки выбирается кликом: ребро без тела повисло бы
    // отдельной пластиной, а смысл его — усилить деталь.
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'rib') return;
        if (selection.kind !== 'body' || selection.ids.length === 0) return;
        if (targetBodyId) return;
        updatePendingFeature({ targetBodyId: selection.ids[0] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    if (!pendingFeature || pendingFeature.kind !== 'rib') return null;

    const sketch = (sketches || []).find((s) => s.id === pendingFeature.sketchId);
    const chains = sketch ? findOpenChains(sketch) : [];
    const chain = chains.find((c) => c.id === pendingFeature.chainId) || chains[0];

    /** Вершины ломаной в координатах эскиза. */
    const chainPoints = () => {
        if (!chain || !sketch) return [];
        const byId = new Map(
            sketch.primitives.filter((p) => p.kind === 'point').map((p) => [p.id, p]),
        );
        return chain.pointIds
            .map((id) => byId.get(id))
            .filter(Boolean)
            .map((p) => [p.x, p.y]);
    };

    const onOk = async () => {
        const pts = chainPoints();
        if (pts.length < 2) {
            toast('В эскизе не нашлось незамкнутой линии для ребра.', 'error');
            return;
        }
        try {
            await getGeometryClient().createRib({
                points: pts,
                planeName: sketch.planeRef?.plane || 'XY',
                planeOffset: sketch.planeRef?.offset || 0,
                customPlane: sketch.planeRef?.plane === 'CUSTOM'
                    ? sketch.planeRef : null,
                thickness,
                depth,
                kind: ribKind,
                targetBodyId,
            });
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                kind: 'rib',
                ribKind,
                sketchId: sketch.id,
                chainId: chain.id,
                thickness,
                depth,
                targetBodyId,
            });
            setPendingFeature(null);
        } catch (e) {
            toast(e?.message || String(e), 'error');
        }
    };

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>
                {ribKind === 'web' ? 'Перемычка' : 'Ребро жёсткости'}
            </div>

            <FormRow label="Линия">
                {chain ? (
                    <span className={cl.profileBadge}>
                        {chain.edgeIds.length} отр.
                    </span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Нужна незамкнутая линия в эскизе
                    </span>
                )}
            </FormRow>

            <FormRow label="Направление">
                <select
                    className={cl.select}
                    value={ribKind}
                    onChange={(e) => updatePendingFeature({ ribKind: e.target.value })}
                >
                    <option value="rib">Ребро — вниз, к телу</option>
                    <option value="web">Перемычка — вверх от эскиза</option>
                </select>
            </FormRow>

            <FormRow label="Толщина">
                <ExprInput
                    value={thickness}
                    unit="мм"
                    min={0.1}
                    onChange={(v) => updatePendingFeature({ thickness: v })}
                />
            </FormRow>

            <FormRow label="Высота">
                <ExprInput
                    value={depth}
                    unit="мм"
                    min={0.1}
                    onChange={(v) => updatePendingFeature({ depth: v })}
                />
            </FormRow>

            <FormRow label="Приварить к">
                {targetBodyId ? (
                    <span className={cl.profileBadge}>{targetBodyId}</span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Кликни по телу (иначе ребро останется отдельной деталью)
                    </span>
                )}
            </FormRow>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!chain}
                >
                    OK
                </button>
                <button
                    className={`${cl.btn} ${cl.btnSecondary}`}
                    onClick={() => setPendingFeature(null)}
                >
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
}
